(() => {
  // Marked on the element itself (not a per-script-execution WeakSet) so that
  // if this file is ever accidentally included more than once on the same
  // page (e.g. a repeated section instance each emitting their own <script
  // src>), a second execution still won't double-wire the same button —
  // every extra execution finds the flag already set and skips it.
  function alreadyInitialized(root, flag) {
    if (root.dataset[flag] === 'true') return true;
    root.dataset[flag] = 'true';
    return false;
  }

  function formatMoney(cents, moneyFormat) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    const amount = (Number(cents) / 100).toFixed(2);
    return String(moneyFormat || '{{ amount }}')
      .replace(/\{\{\s*amount(?:_with_comma_separator)?\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(Number(cents) / 100)));
  }

  function parseJson(root, selector) {
    const element = root.querySelector(selector);
    if (!element) return null;
    try {
      return JSON.parse(element.textContent || 'null');
    } catch (error) {
      console.error('[DigiSnap] Invalid product data', error);
      return null;
    }
  }

  function initGallery(root) {
    if (alreadyInitialized(root, 'dsGalleryInit')) return;

    const track = root.querySelector('[data-ds-gallery-track]');
    const slides = Array.from(root.querySelectorAll('[data-ds-image]'));
    const thumbs = Array.from(root.querySelectorAll('[data-ds-thumb]'));
    const dots = Array.from(root.querySelectorAll('[data-ds-dot]'));
    const counterCurrent = root.querySelector('[data-ds-counter-current]');
    const lightbox = root.closest('[data-ds-buybox]')?.querySelector('[data-ds-lightbox]')
      || root.parentElement?.querySelector('[data-ds-lightbox]')
      || document.querySelector('[data-ds-lightbox]');
    if (!track || slides.length === 0) return;

    let activeIndex = 0;
    let lightboxOpen = false;

    function goTo(index, { scroll = true } = {}) {
      if (index < 0 || index >= slides.length) return;
      activeIndex = index;

      slides.forEach((slide, i) => {
        const isActive = i === index;
        if (isActive) slide.hidden = false;
        slide.classList.toggle('is-current', isActive);
      });
      // Desktop crossfades the outgoing image out before actually hiding it
      // (see .ds-product-image-btn opacity transition in digisnap-product.css);
      // on mobile [hidden] is already overridden to stay visible in the swipe
      // track, so this delay is a no-op there.
      window.clearTimeout(root._dsGalleryHideTimer);
      root._dsGalleryHideTimer = window.setTimeout(() => {
        slides.forEach((slide, i) => {
          if (i !== index) slide.hidden = true;
        });
      }, 300);
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
      if (counterCurrent) counterCurrent.textContent = String(index + 1);

      if (scroll && slides[index]) {
        slides[index].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      }
      if (lightboxOpen) updateLightboxImage();
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = Number.parseInt(thumb.dataset.dsIndex, 10);
        if (Number.isFinite(index)) goTo(index);
      });
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number.parseInt(dot.dataset.dsIndex, 10);
        if (Number.isFinite(index)) goTo(index);
      });
    });

    // Keep active state in sync while the visitor swipes the mobile track,
    // so the thumbnail/dot/counter always reflect the on-screen image.
    if ('IntersectionObserver' in window) {
      const scrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              const index = Number.parseInt(entry.target.dataset.dsIndex, 10);
              if (Number.isFinite(index) && index !== activeIndex) {
                goTo(index, { scroll: false });
              }
            }
          });
        },
        { root: track, threshold: [0.6] }
      );
      slides.forEach((slide) => scrollObserver.observe(slide));
    }

    function updateLightboxImage() {
      if (!lightbox) return;
      const image = lightbox.querySelector('[data-ds-lightbox-image]');
      const source = slides[activeIndex]?.querySelector('img');
      if (image && source) {
        image.src = source.currentSrc || source.src;
        image.alt = source.alt || '';
      }
    }

    function openLightbox(index) {
      if (!lightbox) return;
      lightboxOpen = true;
      goTo(index, { scroll: false });
      updateLightboxImage();
      lightbox.hidden = false;
      document.body.classList.add('ds-lightbox-open');
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightboxOpen = false;
      lightbox.hidden = true;
      document.body.classList.remove('ds-lightbox-open');
    }

    slides.forEach((slide) => {
      // Video/model slides carry their own native controls (play, seek, 3D
      // orbit) — wiring the zoom-lightbox click here on top of them just
      // swallows the tap meant for the player and pops an empty image
      // lightbox instead, since the lightbox only ever displays an <img>.
      if (slide.dataset.dsMediaType) return;
      slide.addEventListener('click', () => {
        const index = Number.parseInt(slide.dataset.dsIndex, 10);
        openLightbox(Number.isFinite(index) ? index : activeIndex);
      });
    });

    if (lightbox) {
      lightbox.querySelector('[data-ds-lightbox-close]')?.addEventListener('click', closeLightbox);
      lightbox.querySelector('[data-ds-lightbox-prev]')?.addEventListener('click', () => {
        goTo((activeIndex - 1 + slides.length) % slides.length, { scroll: false });
      });
      lightbox.querySelector('[data-ds-lightbox-next]')?.addEventListener('click', () => {
        goTo((activeIndex + 1) % slides.length, { scroll: false });
      });
      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox) closeLightbox();
      });
      document.addEventListener('keydown', (event) => {
        if (!lightboxOpen) return;
        if (event.key === 'Escape') closeLightbox();
        if (event.key === 'ArrowLeft') goTo((activeIndex - 1 + slides.length) % slides.length, { scroll: false });
        if (event.key === 'ArrowRight') goTo((activeIndex + 1) % slides.length, { scroll: false });
      });
    }

    goTo(0, { scroll: false });
  }

  function initBuybox(root) {
    if (alreadyInitialized(root, 'dsBuyboxInit')) return;

    const product = parseJson(root, '[data-ds-main-product]');
    const copy = parseJson(root, '[data-ds-copy]') || {};
    const form = root.querySelector('[data-ds-form]');
    const variantInput = root.querySelector('[data-ds-variant-id]');
    const addButton = root.querySelector('[data-ds-add]');
    const buyButton = root.querySelector('[data-ds-buy-now]');
    const mobileButton = root.querySelector('[data-ds-mobile-cta]');
    const quantityInput = root.querySelector('[data-ds-qty]');
    const message = root.querySelector('[data-ds-message]');
    const availability = root.querySelector('[data-ds-availability]');
    const moneyFormat = root.dataset.moneyFormat || '{{ amount }}';
    let requestInFlight = false;

    if (!product || !form || !variantInput || !addButton || !buyButton) return;

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variantById = (id) => variants.find((variant) => String(variant.id) === String(id)) || null;

    function selectedVariant() {
      const radio = root.querySelector('[data-ds-main-variant]:checked');
      const source = radio || variantInput;
      const jsonVariant = variantById(source.value);

      return {
        ...(jsonVariant || {}),
        id: source.value,
        price: Number(source.dataset.price || jsonVariant?.price || 0),
        compare_at_price: Number(source.dataset.comparePrice || jsonVariant?.compare_at_price || 0),
        available: source.dataset.available
          ? source.dataset.available === 'true'
          : Boolean(jsonVariant?.available),
        featured_media: source.dataset.mediaId
          ? { id: source.dataset.mediaId }
          : jsonVariant?.featured_media,
      };
    }

    function selectedAccessories() {
      return Array.from(root.querySelectorAll('[data-ds-accessory]')).map((card) => {
        const toggle = card.querySelector('[data-ds-accessory-toggle]');
        const select = card.querySelector('[data-ds-accessory-select]');
        const hidden = card.querySelector('[data-ds-accessory-variant]');
        const option = select?.selectedOptions?.[0];
        const source = option || hidden;

        return {
          card,
          toggle,
          selected: Boolean(toggle?.checked),
          id: source?.value || '',
          price: Number(source?.dataset.price || 0),
          available: source?.dataset.available === 'true',
        };
      });
    }

    function quantity() {
      const parsed = Number.parseInt(quantityInput?.value || '1', 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    function validation() {
      const variant = selectedVariant();
      if (!variant) return { ok: false, message: copy.unavailable };
      if (!variant.available) return { ok: false, message: copy.soldOut };

      const invalidAccessory = selectedAccessories().find(
        (accessory) => accessory.selected && (!accessory.id || !accessory.available)
      );
      if (invalidAccessory) return { ok: false, message: copy.unavailable };

      return { ok: true, variant };
    }

    function items() {
      const state = validation();
      if (!state.ok) return [];
      const itemQuantity = quantity();
      const selected = [{ id: Number(state.variant.id), quantity: itemQuantity }];

      selectedAccessories().forEach((accessory) => {
        if (accessory.selected) selected.push({ id: Number(accessory.id), quantity: itemQuantity });
      });
      return selected;
    }

    function setMessage(text, isError = false) {
      if (!message) return;
      message.textContent = text || '';
      message.classList.toggle('is-error', isError);
    }

    function setBusy(busy, checkout = false) {
      requestInFlight = busy;
      addButton.disabled = busy;
      buyButton.disabled = busy;
      if (mobileButton) mobileButton.disabled = busy;
      form.toggleAttribute('aria-busy', busy);
      const addLabel = addButton.querySelector('[data-ds-button-label]');
      const buyLabel = buyButton.querySelector('[data-ds-button-label]');
      if (addLabel) addLabel.textContent = busy && !checkout ? copy.adding : copy.add;
      if (buyLabel) buyLabel.textContent = busy && checkout ? copy.checkout : copy.buy;
    }

    function sync() {
      const variant = selectedVariant();
      const isAvailable = Boolean(variant?.available);
      if (variant) variantInput.value = String(variant.id);
      if (variant) {
        variantInput.dataset.price = String(variant.price);
        variantInput.dataset.comparePrice = String(variant.compare_at_price || 0);
        variantInput.dataset.available = String(Boolean(variant.available));
        variantInput.dataset.mediaId = String(variant.featured_media?.id || '');
      }

      root.querySelectorAll('[data-ds-main-variant]').forEach((radio) => {
        radio.closest('.ds-pack-option')?.classList.toggle('is-active', radio.checked);
      });

      root.querySelectorAll('[data-ds-price]').forEach((element) => {
        if (variant) element.textContent = formatMoney(variant.price, moneyFormat);
      });
      root.querySelectorAll('[data-ds-compare]').forEach((element) => {
        const show = Boolean(variant && variant.compare_at_price > variant.price);
        element.hidden = !show;
        if (show) element.textContent = formatMoney(variant.compare_at_price, moneyFormat);
      });

      let total = variant ? Number(variant.price) * quantity() : 0;
      selectedAccessories().forEach((accessory) => {
        accessory.card.classList.toggle('is-selected', accessory.selected);
        accessory.card.classList.toggle('is-unavailable', !accessory.available);
        if (accessory.selected && accessory.available) total += accessory.price * quantity();
        const price = accessory.card.querySelector('[data-ds-accessory-price]');
        if (price) price.textContent = formatMoney(accessory.price, moneyFormat);
      });
      root.querySelectorAll('[data-ds-setup-total]').forEach((element) => {
        element.textContent = formatMoney(total, moneyFormat);
      });

      if (availability) availability.textContent = isAvailable ? copy.available : copy.soldOut;
      if (!requestInFlight) {
        addButton.disabled = !isAvailable;
        buyButton.disabled = !isAvailable;
        if (mobileButton) mobileButton.disabled = !isAvailable;
      }
      setMessage('', false);
    }

    async function refreshCartDrawer() {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-drawer-section`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Unable to refresh the cart drawer.');

      const sections = await response.json();
      const html = sections['cart-drawer-section'];
      const current = document.getElementById('shopify-section-cart-drawer-section');
      if (!html || !current) return false;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.getElementById('shopify-section-cart-drawer-section');
      if (!next) return false;

      current.replaceWith(next);
      await customElements.whenDefined('theme-drawer');
      return true;
    }

    async function refreshCartCount() {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('cart-icon').forEach((icon) => {
        if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(cart.item_count);
      });
    }

    async function openCartDrawer() {
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return true;
      }
      return false;
    }

    async function addSetup(checkout) {
      if (requestInFlight) return;
      const state = validation();
      if (!state.ok) {
        setMessage(state.message || copy.error, true);
        sync();
        return;
      }

      setBusy(true, checkout);
      setMessage(checkout ? copy.checkout : copy.adding);

      try {
        const response = await fetch(root.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: items() }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || copy.error);

        if (checkout) {
          window.location.assign('/checkout');
          return;
        }

        await Promise.all([refreshCartDrawer(), refreshCartCount()]);
        setMessage(copy.added);
        if (!(await openCartDrawer())) window.location.assign(root.dataset.cartUrl || '/cart');
      } catch (error) {
        console.error('[DigiSnap] Add to cart failed', error);
        setMessage(error.message || copy.error, true);
      } finally {
        setBusy(false, checkout);
        sync();
      }
    }

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      addSetup(false);
    });
    buyButton.addEventListener('click', () => addSetup(true));
    root.addEventListener('change', sync);
    quantityInput?.addEventListener('input', sync);

    const mobileBar = root.querySelector('.ds-mobile-buybar');
    const ctaAnchor = root.querySelector('.ds-primary-action');
    if (mobileBar && ctaAnchor && 'IntersectionObserver' in window) {
      const barObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            mobileBar.classList.toggle('is-visible', !entry.isIntersecting);
          });
        },
        { rootMargin: '0px 0px -45% 0px', threshold: 0 }
      );
      barObserver.observe(ctaAnchor);
    }

    sync();
  }

  function init(scope = document) {
    scope.querySelectorAll?.('[data-ds-gallery]').forEach(initGallery);
    scope.querySelectorAll?.('[data-ds-buybox]').forEach(initBuybox);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
